import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../utils/hooks/useAuth';

export interface NavigationStep {
  id: string;
  timestamp: Date;
  path: string;
  searchParams: string;
  hash: string;
  userEmail?: string;
  sessionId: string;
  duration?: number;
  referrer?: string;
  userAgent?: string;
  screenResolution?: string;
  timeOnPage?: number;
}

export interface NavigationSession {
  sessionId: string;
  startTime: Date;
  endTime?: Date;
  steps: NavigationStep[];
  totalDuration: number;
  uniquePages: string[];
  userEmail?: string;
}

interface NavigationContextType {
  navigationSteps: NavigationStep[];
  sessionId: string;
  sessionStats: any;
  clearHistory: () => void;
  exportSessionData: () => any;
  getNavigationDataForDocument: () => any;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationContext must be used within a NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: React.ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [navigationSteps, setNavigationSteps] = useState<NavigationStep[]>([]);
  const [currentSession, setCurrentSession] = useState<NavigationSession | null>(null);
  const previousPathRef = useRef<string>('');
  const sessionStartTimeRef = useRef<Date>(new Date());
  const sessionIdRef = useRef<string>(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const pageStartTimeRef = useRef<Date>(new Date());

  // Inizializza una nuova sessione
  const initializeSession = useCallback(() => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionIdRef.current = newSessionId;
    sessionStartTimeRef.current = new Date();
    pageStartTimeRef.current = new Date();

    const newSession: NavigationSession = {
      sessionId: newSessionId,
      startTime: sessionStartTimeRef.current,
      steps: [],
      totalDuration: 0,
      uniquePages: [],
      userEmail: user?.email
    };

    setCurrentSession(newSession);
    setNavigationSteps([]);
  }, [user?.email]);

  // Aggiunge un nuovo passo di navigazione
  const addNavigationStep = useCallback((path: string, searchParams: string, hash: string) => {
    const now = new Date();
    const timeOnPage = previousPathRef.current ? 
      now.getTime() - pageStartTimeRef.current.getTime() : 
      0;

    const newStep: NavigationStep = {
      id: `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: now,
      path,
      searchParams,
      hash,
      userEmail: user?.email,
      sessionId: sessionIdRef.current,
      duration: previousPathRef.current ? 
        now.getTime() - (navigationSteps[navigationSteps.length - 1]?.timestamp.getTime() || now.getTime()) : 
        undefined,
      referrer: previousPathRef.current || document.referrer,
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timeOnPage
    };

    setNavigationSteps(prev => {
      const updatedSteps = [...prev, newStep];
      
      // Aggiorna la sessione corrente
      if (currentSession) {
        const uniquePages = Array.from(new Set([...currentSession.uniquePages, path]));
        const updatedSession: NavigationSession = {
          ...currentSession,
          steps: updatedSteps,
          uniquePages,
          totalDuration: now.getTime() - sessionStartTimeRef.current.getTime()
        };
        setCurrentSession(updatedSession);
      }

      return updatedSteps;
    });

    pageStartTimeRef.current = now;
  }, [navigationSteps, currentSession, user?.email]);

  // Traccia la navigazione quando cambia la location
  useEffect(() => {
    const currentPath = location.pathname;
    
    if (previousPathRef.current && previousPathRef.current !== currentPath) {
      addNavigationStep(
        currentPath, 
        location.search, 
        location.hash
      );
    } else if (!previousPathRef.current) {
      // Primo accesso - inizializza sessione
      initializeSession();
      addNavigationStep(
        currentPath, 
        location.search, 
        location.hash
      );
    }
    
    previousPathRef.current = currentPath;
  }, [location, addNavigationStep, initializeSession]);

  // Gestisce la chiusura della sessione
  const endSession = useCallback(() => {
    if (currentSession) {
      const endTime = new Date();
      const updatedSession: NavigationSession = {
        ...currentSession,
        endTime,
        totalDuration: endTime.getTime() - sessionStartTimeRef.current.getTime()
      };
      setCurrentSession(updatedSession);
    }
  }, [currentSession]);

  // Pulisce la cronologia
  const clearHistory = useCallback(() => {
    setNavigationSteps([]);
    if (currentSession) {
      setCurrentSession({
        ...currentSession,
        steps: []
      });
    }
  }, [currentSession]);

  // Ottiene statistiche della sessione
  const getSessionStats = useCallback(() => {
    if (!currentSession) return null;

    const avgTimePerPage = currentSession.steps.length > 0 ? 
      currentSession.totalDuration / currentSession.steps.length : 0;

    const mostVisitedPage = currentSession.steps.reduce((acc, step) => {
      acc[step.path] = (acc[step.path] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    const topPage = Object.entries(mostVisitedPage)
      .sort(([,a], [,b]) => b - a)[0];

    return {
      totalSteps: currentSession.steps.length,
      uniquePages: currentSession.uniquePages.length,
      totalDuration: currentSession.totalDuration,
      avgTimePerPage,
      mostVisitedPage: topPage ? { path: topPage[0], visits: topPage[1] } : null,
      sessionDuration: currentSession.endTime ? 
        currentSession.endTime.getTime() - currentSession.startTime.getTime() : 
        Date.now() - currentSession.startTime.getTime()
    };
  }, [currentSession]);

  // Esporta i dati della sessione
  const exportSessionData = useCallback(() => {
    if (!currentSession) return null;

    return {
      session: currentSession,
      stats: getSessionStats(),
      exportTime: new Date().toISOString()
    };
  }, [currentSession, getSessionStats]);

  // Ottiene i dati di navigazione in formato JSON per l'invio con i documenti
  const getNavigationDataForDocument = useCallback(() => {
    if (!currentSession) return null;

    return {
      navigationHistory: {
        sessionId: currentSession.sessionId,
        startTime: currentSession.startTime.toISOString(),
        endTime: currentSession.endTime?.toISOString(),
        totalSteps: currentSession.steps.length,
        uniquePages: currentSession.uniquePages,
        totalDuration: currentSession.totalDuration,
        steps: currentSession.steps.map(step => ({
          id: step.id,
          timestamp: step.timestamp.toISOString(),
          path: step.path,
          searchParams: step.searchParams,
          hash: step.hash,
          duration: step.duration,
          timeOnPage: step.timeOnPage,
          referrer: step.referrer,
          userAgent: step.userAgent,
          screenResolution: step.screenResolution
        })),
        stats: getSessionStats()
      },
      exportTime: new Date().toISOString(),
      userEmail: user?.email
    };
  }, [currentSession, getSessionStats, user?.email]);

  // Gestisce la chiusura della finestra
  useEffect(() => {
    const handleBeforeUnload = () => {
      endSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [endSession]);

  const value: NavigationContextType = {
    navigationSteps,
    sessionId: sessionIdRef.current,
    sessionStats: getSessionStats(),
    clearHistory,
    exportSessionData,
    getNavigationDataForDocument
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}; 